import { useCallback } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useDigitalProductContextOptional } from '@/shared/contexts/DigitalProductContext'
import { api } from '@/shared/lib/api'

export type WinnerAnnouncement = {
  id: number
  name: string
  announced_at: string
  media?: { file?: { url?: string } } | null
}

type AnnouncementsResponse = { records: WinnerAnnouncement[] }

const EMPTY: WinnerAnnouncement[] = []

/**
 * Janela em que uma resposta já buscada continua valendo.
 *
 * Este é o **único** endereço REST autenticado que a tela inicial do
 * participante consulta — todo o resto dela vem do conteúdo versionado do
 * evento, que é servido sem autenticação e cacheado em CDN. Num evento de 20
 * mil pessoas, cada montagem da home sem cache é uma requisição que paga
 * construção de ability no servidor para, em mais de 99% dos casos, responder
 * lista vazia.
 *
 * Sessenta segundos é o mesmo patamar dos outros hooks do projeto e cobre o que
 * mais acontece: sair para Jornadas e voltar, ou trocar de aba, sem refazer a
 * pergunta.
 */
const STALE_TIME = 60_000

/**
 * Concursos que o participante ganhou e ainda não deu ciência.
 *
 * Vem de endereço **autenticado**, e não do conteúdo versionado do evento, por
 * duas razões que se somam: ciência é dado por pessoa, e aquele conteúdo é
 * servido sem autenticação e cacheado em CDN — publicá-lo ali diria a qualquer
 * um quem ganhou o quê e se já viu.
 *
 * O efeito colateral é bem-vindo: entre o anúncio e a reconstrução do conteúdo
 * do evento existe uma janela, e este endereço já sabe do resultado enquanto o
 * conteúdo ainda não. O aviso aparece na hora certa.
 *
 * **Sem `refetchInterval`, de propósito.** Pesquisar de tempos em tempos
 * pareceria a forma de o aviso chegar sozinho, e seria o pior jeito: 20 mil
 * pessoas perguntando a cada minuto são centenas de requisições por segundo
 * sustentadas, para quase sempre responder vazio. O aviso chega na próxima
 * montagem ou quando o app volta ao foco — que é também quando o participante
 * está olhando, e é a mesma frescura que a versão anterior deste hook entregava.
 */
export const useWinnerAnnouncements = () => {
  const digitalProductId =
    useDigitalProductContextOptional()?.digitalProductId ?? null

  const queryClient = useQueryClient()
  const queryKey = ['prize-announcements', digitalProductId]

  const basePath = digitalProductId
    ? `/user/digital_products/${digitalProductId}/prize_announcements`
    : null

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data } = await api.get<AnnouncementsResponse>(basePath!)

      return data?.records ?? EMPTY
    },
    enabled: !!basePath,
    staleTime: STALE_TIME,
    // Aviso é acessório: se a busca falha, a tela inicial continua inteira.
    // Insistir com alerta transformaria uma falha de rede num obstáculo entre o
    // participante e o evento.
    retry: false
  })

  const announcements = data ?? EMPTY

  /**
   * Registra ciência. Some com o aviso de imediato, antes da resposta: o gesto
   * já aconteceu na tela e esperar o servidor deixaria a faixa piscando.
   *
   * Recusa significa aviso velho — outra aba já deu ciência, ou o concurso
   * mudou de estado. A resposta certa é recarregar a lista, não alertar.
   */
  const acknowledge = useCallback(
    async (prizeId: number) => {
      if (!basePath) return

      queryClient.setQueryData<WinnerAnnouncement[]>(queryKey, current =>
        (current ?? EMPTY).filter(item => item.id !== prizeId)
      )

      try {
        await api.post(`${basePath}/${prizeId}/acknowledge`)
      } catch {
        await queryClient.invalidateQueries({ queryKey })
      }
    },
    // `queryKey` é recriado a cada render; o que de fato identifica a consulta é
    // o id do evento, e é por ele que a dependência anda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basePath, digitalProductId, queryClient]
  )

  return { announcements, isLoading, acknowledge, refetch }
}
